import { Injectable, OnDestroy } from '@angular/core';
import { ITableData } from '../../../models/reports/ITableData';
import { ReportPageOrientation } from '../../../models/reports/ReportPageOrientation';
import { ReportList } from '../../../models/dtos/Enums/ReportList';
import { IReportData } from '../../../models/reports/IReportData';
import { EstimateGrossMargin, 
  EstimateMetaData, 
  GetDeviceAndPartIVKRequest, 
  GetDeviceAndPartIVKResponse, 
  GetPartTypeIdsResponse, 
  Material, 
  MaterialData, 
  SQWebArea, 
  SQWebSystem, 
  SubSystem, 
  SystemChildren, TypicalOfSystemHelper } from '../../../models/dtos';
import { TranslateService } from '@ngx-translate/core';
import { CONSTANTS } from '../../../Constants/Constants';
import { IData } from '../../../models/reports/IData';
import { MaterialReportType } from '../../../models/enum';
import { IDataItem } from '../../../models/reports/IDataItem';
import { IFooter } from '../../../models/reports/IFooter';
import { SQWebAreaChildren } from '../../../models/SQWebAreaChildren';
import { MultilingualCurrencyPipe } from '../../../pipes/multilingual-currency-pipe/multilingual-currency.pipe';
import { ReportsAPIService } from '../../api/reports-api.service';
import { firstValueFrom } from 'rxjs';
import { MaterialReportData } from '../../../models/reports/MaterialReportData';
import { MaterialSummaryData } from '../../../models/reports/MaterialSummaryData';
import { ReportsCommonService } from '../reports-common/reports-common.service';
import { CommonDataService } from '../../common-data.service';
import { IndexDBService } from '../../common/indexDB.service';
import { ReportCheckboxes } from '../../../models/reports/ReportCheckboxes';
import { DecimalPrecisionPipe } from '../../../pipes/decimal-precision-pipe/decimal-precision.pipe';

@Injectable()
export class SolMaterialRelatedReportsService implements OnDestroy {
  projectAreas: SQWebArea[] = [];
  projectMaterialData: Record<string, MaterialData>;
  materialByPartTypeData: ITableData[] = [];
  materialByTypeData: ITableData[] = [];
  serviceContractMaterialData: ITableData[] = [];
  formattedMaterialReportData: Record<string, MaterialReportData> = {};
  siemensMaterialReportData: Record<string, MaterialReportData> = {};
  siOemMaterialReportData: Record<string, MaterialReportData> = {};
  outsideMaterialReportData: Record<string, MaterialReportData> = {};
  mechanicalMaterialReportData: Record<string, MaterialReportData> = {};
  electricalMaterialReportData: Record<string, MaterialReportData> = {};
  collectionByPartType: Record<string, MaterialReportData[]> = {};
  estimateMetaData: EstimateMetaData;
  consolidatedMaterialSummaryData: Record<string, MaterialSummaryData> = {};
  partTypeIdNameMap: Record<string, string> = {};
  ssnWithIVKMap: Record<string, string> = {};
  grossMargin: EstimateGrossMargin;
  selectedReportCheckboxes: ReportCheckboxes;
  currentReportId: ReportList = 0 as ReportList;
  private typicalOfHelper: Record<string, TypicalOfSystemHelper> = {};

  constructor(
    private readonly translate: TranslateService,
    private readonly commonDataService: CommonDataService,
    private readonly reportsCommonService: ReportsCommonService,
    private readonly multiLingualPipe: MultilingualCurrencyPipe,
    private readonly decimalPrecisionPipe: DecimalPrecisionPipe,
    private readonly materialRelatedReportsAPIService: ReportsAPIService,
    private readonly indexDBService: IndexDBService
  ) { }

  async generateReport(reportId: ReportList, pageOrientation: ReportPageOrientation = CONSTANTS.REPORT_PAGEORIENTATION_PORTRAIT, internalReport: boolean = true) {
    this.formattedMaterialReportData = {};
    this.selectedReportCheckboxes = this.reportsCommonService.getReportsCheckboxes();
    await this.indexDBService.getAllMaterialData().then(data => {
        this.projectMaterialData = data.materialsCollection;
      });
    this.projectAreas = this.commonDataService.getSQWebProject()?.areas || [];
    this.estimateMetaData = this.commonDataService.getEstimateMetaData();
    this.grossMargin = this.commonDataService.getOtherMetaData()?.grossMargin;
    this.currentReportId = reportId;
    this.typicalOfHelper = this.commonDataService.getTypicalOfHelpers();
    this.generateSequencedMaterialList();
    const tableDataResult = this.generateTableData(reportId);
    if (tableDataResult instanceof Promise) {
      tableDataResult.then((tableData: ITableData[]) => {
        this.generateReportData(reportId, pageOrientation, tableData, internalReport);
      });
    } else {
      this.generateReportData(reportId, pageOrientation, tableDataResult as ITableData[], internalReport);
    }
  }

  generateReportData(reportId: ReportList, pageOrientation: ReportPageOrientation, tableData: ITableData[], internalReport: boolean = false) {
    const reportName = this.reportsCommonService.getSelectedReportName();
    const reportHeaderData = this.commonDataService.getReportHeaderData();
    let reportData: IReportData = {
      selectionName: this.translate.instant(reportName),
      reportTitle: this.translate.instant(reportName),
      reportTitles: {
        data: this.reportsCommonService.generateReportTitles(reportHeaderData)
      },
      tableData: tableData,
      pageOrientation: pageOrientation,
      internalReport: internalReport
    }
    this.reportsCommonService.setSelectedReport(reportData);
  }

  generateTableData(reportId: ReportList): Promise<ITableData[]> | ITableData[] {
    switch (reportId) {
      case ReportList.MaterialByPartTypeViewModel:
        return this.generateMaterialByPartTypeReportData();
      case ReportList.MaterialByTypeViewModel:
        return this.generateMaterialByTypeReportData();
      case ReportList.ServiceContractMaterialViewModel:
        return this.generateServiceContractMaterialReportData();
      default:
        return [];
    }
  }

  generateSequencedMaterialList() {
    this.projectAreas.forEach((area) => {
      this.iterateArea(area);
    });
  }

  iterateArea(area: SQWebArea) {
    if (area?.isItemVisible && area?.children) {
      for (let areaChild of area.children as SQWebAreaChildren[]) {
        if (areaChild.nodeType !== CONSTANTS.NODETYPE_CONTROLLER && !areaChild.isItemVisible) {
          continue;
        }
        if (areaChild?.nodeType && areaChild?.itemUniqueId) {
          if (areaChild.nodeType) {
            if (areaChild.nodeType === 'Area') {
              this.iterateArea(areaChild as SQWebArea);
            } else if(areaChild.nodeType ===  CONSTANTS.NODETYPE_BSETSYSTEM) {
              this.handleTypicalOf(areaChild as SQWebSystem);
            } else {
              this.formatReportData(areaChild.itemUniqueId);  
            }
          }
        }
      }
    }
  }

  handleTypicalOf(system: SQWebSystem){
    if(system.hasTypicalOf) {
      const typicalId = system.systemTypicalOfHelperUniqueId;
      const typicalOf = this.typicalOfHelper[typicalId];
      const typicalOfUniqueId = typicalOf?.typicalOfCollectionHolderId ?? system.itemUniqueId;
      this.formatReportData(typicalOfUniqueId);
    } else {
      this.formatReportData(system.itemUniqueId);
    }
  }


  formatReportData(materialUniqueId: string, typicalOfCount?: number) {
    let materialItemsCollection = this.projectMaterialData[materialUniqueId].systemChildrenCollection;
    this.formatMaterialData(materialItemsCollection, typicalOfCount);
  }

  formatMaterialData(materialItemsCollection: SystemChildren, typicalOfCount?: number) {
    materialItemsCollection?.forEach((materialItem) => {
      if (materialItem.systemComponentNodeType === CONSTANTS.NODETYPE_SUBSYSTEM) {
        (materialItem as SubSystem).children.forEach((childMaterialItem) => {
          this.generateMaterialReportData(childMaterialItem as Material);
        });
      } else {
        this.generateMaterialReportData(materialItem as Material, typicalOfCount);
      }
    });
  }

  private getDescription(materialItem: Material): string {
    if(this.reportsCommonService.isGenericNameApplicable()) {
      return this.reportsCommonService.getGenericNameBySSN(materialItem.ssn) || materialItem.description;
    }
    return materialItem.description;
  }

  generateMaterialReportData(materialItem: Material, typicalOfCount?: number) {
   let description = this.getDescription(materialItem);
    let descriptionKey = description.split(' ').join('_');
    let key = this.selectedReportCheckboxes.expanded.checked ?
      `${materialItem.referenceNumber}_${materialItem.modelNumber}_${materialItem.manufacturerName}_${descriptionKey}`
      : `${materialItem.referenceNumber}_${descriptionKey}`;
    const isMaterialEligibleForCurrentReport = this.isMaterialEligibleForCurrentReport(materialItem);

    if (this.currentReportId === ReportList.MaterialByTypeViewModel) {
      key = `${key}_${materialItem.cost}_${materialItem.materialType}`;
    }

    if (this.formattedMaterialReportData[key] && isMaterialEligibleForCurrentReport && !typicalOfCount) {
      this.formattedMaterialReportData[key].quantity += materialItem.quantity;
      this.formattedMaterialReportData[key].totalCost += materialItem.cost * materialItem.quantity;
    } else if (!this.formattedMaterialReportData[key] && isMaterialEligibleForCurrentReport) {
      this.formattedMaterialReportData[key] = {
        referenceNo: materialItem.referenceNumber,
        modelNo: materialItem.modelNumber || CONSTANTS.EMPTY_STRING,
        manufacturerPartNo: materialItem.mpn || CONSTANTS.EMPTY_STRING,
        description: description,
        quantity: typicalOfCount || materialItem.quantity,
        unitCost: materialItem.cost,
        totalCost: materialItem.cost * (typicalOfCount || materialItem.quantity),
        materialType: materialItem.materialType,
        ivk: materialItem.ivk,
        partTypeID: materialItem.partTypeID,
        isRPN: materialItem.isRPN,
        ssn: materialItem.ssn,
        guid: materialItem.guid,
        materialIndicator: this.reportsCommonService.generateMaterialIndicator(materialItem)
      }
    }
  }

  private isMaterialEligibleForCurrentReport(materialItem: Material): boolean {
    if (!materialItem.isMaster || materialItem.quantity <= CONSTANTS.ZERO) {
      return false;
    }

    if (this.currentReportId === ReportList.ServiceContractMaterialViewModel) {
      return true;
    }

    return materialItem.cost > CONSTANTS.ZERO;
  }

  generateMaterialByPartTypeReportData(): Promise<ITableData[]> {
    this.materialByPartTypeData = [];
    return this.getPartTypeIdsNames();
  }


  async getPartTypeIdsNames(): Promise<ITableData[]> {
  try {
    const response: Array<GetPartTypeIdsResponse> = await firstValueFrom(
      this.materialRelatedReportsAPIService.getPartTypeIdsNames()
    );
    this.partTypeIdNameMap = this.generatePartTypeIdNameMap(response);
    this.arrangeDataAccordingToPartType();
    for (let partTypeKey in this.collectionByPartType) {
      this.generateMaterialByPartTypeReportTableData(partTypeKey);
    }
    return this.materialByPartTypeData;
  } catch (error) {
    throw error;
  }
}

  generatePartTypeIdNameMap(response: Array<GetPartTypeIdsResponse>) {
    let partTypeIdNameMap: Record<string, string> = {};
    response.forEach(partType => {
      partTypeIdNameMap[partType.id] = partType.description;
    })
    return partTypeIdNameMap;
  }

  arrangeDataAccordingToPartType() {
    this.collectionByPartType = {};
    Object.values(this.formattedMaterialReportData).forEach(materialData => {
      if (!this.collectionByPartType[materialData.partTypeID]) {
        this.collectionByPartType[materialData.partTypeID] = [];
      }
      this.collectionByPartType[materialData.partTypeID].push(materialData);
    });
  }

  generateMaterialByPartTypeReportTableData(partTypeKey: string) {
    let materialByPartTypeDataRows: IData[] = this.generateMaterialByPartTypeDataRows(partTypeKey);
    let materialByPartTypeData: ITableData = {
      title: [
        { data: this.translate.instant('QTY'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0 },
        { data: this.translate.instant('MATERIAL_REPORT_MATERIAL_REFERENCE'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0 },
        { data: this.translate.instant('MATERIAL_REPORT_MODEL'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, isExpanded: true },
        { data: this.translate.instant('MATERIAL_REPORT_MANUFACTURER_PART'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, isExpanded: true },
        { data: this.translate.instant('MATERIAL_REPORT_DESCRIPTION'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, width: "200" }
      ],
      tableHeader: [
        {
          headers: [
            { headerText: this.partTypeIdNameMap[partTypeKey] ? this.partTypeIdNameMap[partTypeKey] : CONSTANTS.UNDEFINED, columnspan: 5 }
          ]
        }
      ],
      data: materialByPartTypeDataRows,
      tableFooter: [],
      isTableBordered: true
    };
    this.materialByPartTypeData.push(materialByPartTypeData);
  }

  generateMaterialByPartTypeDataRows(partTypeKey: string): IData[] {
    let materialByPartTypeDataRows: IData[] = [];
    for (let materialKey in this.collectionByPartType[partTypeKey]) {
      materialByPartTypeDataRows.push(this.createMaterialByPartTypeRow(this.collectionByPartType[partTypeKey][materialKey]));
    }
    return materialByPartTypeDataRows;
  }

  createMaterialByPartTypeRow(materialReportData: MaterialReportData): IData {
    let rowData: IData = {
      data: [
        { data: materialReportData.quantity.toString(), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: "center" },
        { data: materialReportData.referenceNo, isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: "left" , materialIndicator: materialReportData.materialIndicator},
        { data: materialReportData.modelNo, isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: "left", isExpanded: true },
        { data: materialReportData.manufacturerPartNo, isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: "left", isExpanded: true },
        { data: materialReportData.description, isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: "left" }
      ]
    };
    return rowData;
  }


  generateServiceContractMaterialReportData(): Promise<ITableData[]> {
    this.serviceContractMaterialData = [];
    return this.getIVKForRespectiveSSN();
  }

  async getIVKForRespectiveSSN(): Promise<ITableData[]> {
    let apnSSNList = CONSTANTS.EMPTY_STRING;
    let rpnSSNList = CONSTANTS.EMPTY_STRING;
    let guidList = CONSTANTS.EMPTY_STRING;
    for (let materialKey in this.formattedMaterialReportData) {
      if (this.formattedMaterialReportData[materialKey].isRPN) {
        rpnSSNList = `${rpnSSNList},${this.formattedMaterialReportData[materialKey].ssn}`;
        guidList = `${guidList},${this.formattedMaterialReportData[materialKey].guid}`;
      } else {
        apnSSNList = `${apnSSNList},${this.formattedMaterialReportData[materialKey].ssn}`;
      }
    }
    let requestBody: GetDeviceAndPartIVKRequest = {
      apnssnList: apnSSNList.slice(1),
      rpnssnList: rpnSSNList.slice(1),
      guidList: guidList.slice(1)
    }
    try{
      const response: Array<GetDeviceAndPartIVKResponse> = await firstValueFrom(this.materialRelatedReportsAPIService.getDeviceAndPartIVK(requestBody));
      for (let ssnKey of response) {
        this.ssnWithIVKMap[ssnKey.ssn] = ssnKey.ivk;
      }
      this.generateServiceByContractMaterialTableData();
      return this.serviceContractMaterialData;;
      } catch (error) {
        throw error;
      }
    }

  generateServiceByContractMaterialTableData() {
    let serviceContractMaterialDataRows: IData[] = this.generateServiceContractMaterialDataRows();
    let serviceContractMaterialData: ITableData = {
      title: [
        { data: this.translate.instant('MATERIAL_REPORT_MATERIAL_REFERENCE'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR },
        { data: this.translate.instant('MATERIAL_REPORT_MODEL'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, isExpanded: true, DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR },
        { data: this.translate.instant('MATERIAL_REPORT_MANUFACTURER_PART'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, isExpanded: true },
        { data: this.translate.instant('MATERIAL_REPORT_DESCRIPTION'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, width: "200" },
        { data: this.translate.instant('QTY'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0 },
        { data: this.translate.instant('MATERIAL_REPORT_IVK'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0 }
      ],
      data: serviceContractMaterialDataRows,
      tableFooter: [],
      isTableBordered: true
    };
    this.serviceContractMaterialData.push(serviceContractMaterialData);
  }

  generateServiceContractMaterialDataRows(): IData[] {
    let serviceContractMaterialDataRows: IData[] = [];
    for (let materialKey in this.formattedMaterialReportData) {
      const item = this.formattedMaterialReportData[materialKey];
      if (this.ssnWithIVKMap[item.ssn] === CONSTANTS.IVK_B) {
        serviceContractMaterialDataRows.push(this.createServiceContractRowData(item));
      }
    }
    return serviceContractMaterialDataRows;
  }

  createServiceContractRowData(materialReportData: MaterialReportData): IData {
    let rowData: IData = {
      data: [
        { data: materialReportData.referenceNo, isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: "left" , materialIndicator: materialReportData.materialIndicator },
        { data: materialReportData.modelNo, isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: "left", isExpanded: true },
        { data: materialReportData.manufacturerPartNo, isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: "left", isExpanded: true },
        { data: materialReportData.description, isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: "left" },
        { data: materialReportData.quantity.toString(), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: "center" },
        { data: this.ssnWithIVKMap[materialReportData.ssn], isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: "center" }
      ]
    };
    return rowData;
  }

  generateMaterialByTypeReportData(): ITableData[] {
    this.materialByTypeData = [];
    this.reArrangeAllDataOnMaterialType();
    this.summaryTable();
    this.materialTableCreation("SIEMENS_MATERIAL", this.siemensMaterialReportData);
    this.materialTableCreation("SI_OEM_MATERIAL", this.siOemMaterialReportData);
    this.materialTableCreation("OUTSIDE_MATERIAL", this.outsideMaterialReportData);
    this.materialTableCreation("MECHANICAL_MATERIAL", this.mechanicalMaterialReportData);
    this.materialTableCreation("ELECTRICAL_MATERIAL", this.electricalMaterialReportData);
    return this.materialByTypeData;
  }

  reArrangeAllDataOnMaterialType() {
    this.siemensMaterialReportData = {};
    this.siOemMaterialReportData = {};
    this.outsideMaterialReportData = {};
    this.mechanicalMaterialReportData = {};
    this.electricalMaterialReportData = {};
    for (let materialKey in this.formattedMaterialReportData) {
      switch (this.formattedMaterialReportData[materialKey].materialType) {
        case MaterialReportType.Siemens_Material:
        case MaterialReportType.Siemens_Material_FR:
          this.siemensMaterialReportData[materialKey] = this.formattedMaterialReportData[materialKey];
          break;
        case MaterialReportType.SI_OEM_Material:
        case MaterialReportType.SI_OEM_Material_FR:
          this.siOemMaterialReportData[materialKey] = this.formattedMaterialReportData[materialKey];
          break;
        case MaterialReportType.Outside_Material:
        case MaterialReportType.Outside_Material_FR:
          this.outsideMaterialReportData[materialKey] = this.formattedMaterialReportData[materialKey];
          break;
        case MaterialReportType.Mechanical_Material:
        case MaterialReportType.Mechanical_Material_FR:
          this.mechanicalMaterialReportData[materialKey] = this.formattedMaterialReportData[materialKey];
          break;
        case MaterialReportType.Electrical_Material:
        case MaterialReportType.Electrical_Material_FR:
          this.electricalMaterialReportData[materialKey] = this.formattedMaterialReportData[materialKey];
          break;
        default:
          break;
      }
    }
  }
  formatSummaryMaterialDataRows() {
    let summaryMaterialDataRows: IData[] = [];
    this.consolidatedMaterialSummaryData = {};
    const summaryMaterialTypes: Record<string, MaterialReportType> = {
      siemensMaterial: MaterialReportType.Siemens_Material,
      outsideMaterial: MaterialReportType.Outside_Material,
      mechanicalMaterial: MaterialReportType.Mechanical_Material,
      electricalMaterial: MaterialReportType.Electrical_Material,
      shippingHandling: MaterialReportType.SHIPPING_HANDLING,
      warranty: MaterialReportType.WARRANTY
    };
    for (const [materialKey, materialType] of Object.entries(summaryMaterialTypes)) {
      const summaryData = this.calculateCostTdcEscalation(materialType);
      if (summaryData.cost !== CONSTANTS.ZERO || summaryData.sellPrice !== CONSTANTS.ZERO) {
        this.consolidatedMaterialSummaryData[materialKey] = summaryData;
      }
    }

    for (let materialKey in this.consolidatedMaterialSummaryData) {
      summaryMaterialDataRows.push(this.createSummaryRowData(this.consolidatedMaterialSummaryData[materialKey]));
    }
    return summaryMaterialDataRows;
  }
  calculateSellPrice(cost: number, gmPercent: number): number {
    let isFixedPriceSelected = this.estimateMetaData.estimateTab.isFixedPriceSelected;
    let isMarkUp = this.estimateMetaData.estimateTab.isMarkUpSelected;
    let soldAsGMPercent = this.grossMargin.soldASGMPercent;
    let price = isFixedPriceSelected ? soldAsGMPercent : gmPercent;
    if (!isMarkUp) {
      price = price === CONSTANTS.HUNDRED ? CONSTANTS.NINETY_NINE_POINT_NINE_NINE : price;
      return cost / (1 - (price / CONSTANTS.HUNDRED));
    } else {
      return cost * (1 + (price / CONSTANTS.HUNDRED));
    }

  }

  calculateCostTdcEscalation(material: string): MaterialSummaryData {
    let materialName: string = CONSTANTS.EMPTY_STRING;
    let cost = 0;
    let escalation = 0;
    let sellPrice = 0;
    let gm = 0;
    let escalationData = this.estimateMetaData.escalationRates;
    switch (material) {
      case MaterialReportType.Siemens_Material:
        cost = this.getMaterialCategoryCost('siemensMaterialCategory', this.estimateMetaData.estimateTab.siemensMaterialCost);
        escalation = escalationData.siemensMaterialEscalation.escalationEnabled ?
          escalationData.siemensMaterialEscalation.escalatedCost - escalationData.siemensMaterialEscalation.baseCost : 0;
        materialName = this.translate.instant('MATERIAL_REPORT_SIEMENS_MATERIAL');
        sellPrice = this.calculateSellPrice(cost, this.grossMargin.siemensMaterialGMPercent);
        gm = sellPrice - cost;
        break;
      case MaterialReportType.Outside_Material:
        cost = this.getMaterialCategoryCost('outsideMaterialCategory', this.estimateMetaData.estimateTab.outsideMaterialCost);
        escalation = escalationData.outsideMaterialEscalation.escalationEnabled ?
          escalationData.outsideMaterialEscalation.escalatedCost - escalationData.outsideMaterialEscalation.baseCost : 0;
        materialName = this.translate.instant('MATERIAL_REPORT_OUTSIDE_MATERIAL');
        sellPrice = this.calculateSellPrice(cost, this.grossMargin.outsideMaterialGMPercent);
        gm = sellPrice - cost;
        break;
      case MaterialReportType.Mechanical_Material:
        cost = this.getMaterialCategoryCost('mechanicalMaterialCategory', this.estimateMetaData.estimateTab.mechanicalMaterialCost);
        escalation = escalationData.mechanicalMaterialEscalation.escalationEnabled ?
          escalationData.mechanicalMaterialEscalation.escalatedCost - escalationData.mechanicalMaterialEscalation.baseCost : 0;
        materialName = this.translate.instant('MATERIAL_REPORT_MECHANICAL_MATERIAL');
        sellPrice = this.calculateSellPrice(cost, this.grossMargin.mechanicalMaterialGMPercent);
        gm = sellPrice - cost;
        break;
      case MaterialReportType.Electrical_Material:
        cost = this.getMaterialCategoryCost('electricalMaterialCategory', this.estimateMetaData.estimateTab.electricalMaterialCost);
        escalation = escalationData.electricalMaterialEscalation.escalationEnabled ?
          escalationData.electricalMaterialEscalation.escalatedCost - escalationData.electricalMaterialEscalation.baseCost : 0;
        materialName = this.translate.instant('MATERIAL_REPORT_ELECTRICAL_MATERIAL');
        sellPrice = this.calculateSellPrice(cost, this.grossMargin.electricalMaterialGMPercent);
        gm = sellPrice - cost;
        break;
      case MaterialReportType.SHIPPING_HANDLING:
        cost = this.estimateMetaData.estimateTab.totalShippingCost;
        materialName = this.translate.instant('MATERIAL_REPORT_SHIPPING_HANDLING')
        sellPrice = this.calculateSellPrice(cost, this.grossMargin.subTotalGMPercent);
        gm = sellPrice - cost;
        break;
      case MaterialReportType.WARRANTY:
        cost = this.estimateMetaData.estimateTab.totalWarrantyCost;
        materialName = this.translate.instant('MATERIAL_REPORT_WARRANTY');
        sellPrice = this.calculateSellPrice(cost, this.grossMargin.subTotalGMPercent);
        gm = sellPrice - cost;
        break;
      default:
        break;
    }
    return {
      materialName: materialName,
      cost: cost,
      tdcPercent: this.estimateMetaData.estimateTab.subTotalsCost ? (cost / this.estimateMetaData.estimateTab.subTotalsCost) * CONSTANTS.HUNDRED : 0,
      escalation: escalation,
      gm: gm,
      sellPrice: sellPrice
    };
  }

  private getMaterialCategoryCost(
    category: 'siemensMaterialCategory' | 'outsideMaterialCategory' | 'mechanicalMaterialCategory' | 'electricalMaterialCategory',
    fallbackCost: number
  ): number {
    return this.commonDataService.getSQWebProject()?.[category]?.sumUpCost ?? fallbackCost;
  }

  summaryTable(): void {
    if (!Object.keys(this.formattedMaterialReportData).length) {
      this.consolidatedMaterialSummaryData = {};
      return;
    }

    let summaryMaterialDataRows: IData[] = this.formatSummaryMaterialDataRows();
    if (!summaryMaterialDataRows.length) {
      return;
    }

    let summaryMaterialData: ITableData = {
      title: [
        { data: this.translate.instant('MATERIAL_REPORT_MATERIAL'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR, width: "100", italics: true },
        { data: this.translate.instant('MATERIAL_REPORT_TDC_PERCENT'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0 },
        { data: this.translate.instant('MATERIAL_REPORT_COST'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0 },
        { data: this.translate.instant('MATERIAL_REPORT_ESCALATION'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0 },
        { data: this.translate.instant('MATERIAL_REPORT_GM'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0 },
        { data: this.translate.instant('MATERIAL_REPORT_SELL_PRICE'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0 }
      ],
      data: summaryMaterialDataRows,
      tableFooter: this.summaryTableFooter(),
      isTableBordered: true
    };
    this.materialByTypeData.push(summaryMaterialData);
  }

  summaryTableFooter(): IFooter[] {
    let totalTdcPercent = CONSTANTS.ZERO,
      totalCost = CONSTANTS.ZERO,
      totalEscalation = CONSTANTS.ZERO,
      totalGm = CONSTANTS.ZERO,
      totalSellPrice = CONSTANTS.ZERO;
    for (let materialKey in this.consolidatedMaterialSummaryData) {
      totalTdcPercent += this.consolidatedMaterialSummaryData[materialKey].tdcPercent;
      totalCost += this.consolidatedMaterialSummaryData[materialKey].cost;
      totalEscalation += this.consolidatedMaterialSummaryData[materialKey].escalation;
      totalGm += this.consolidatedMaterialSummaryData[materialKey].gm;
      totalSellPrice += this.consolidatedMaterialSummaryData[materialKey].sellPrice;
    }
    return [
      {
        headers: [
          { headerText: this.translate.instant('MATERIAL_REPORT_TOTAL_MATERIAL_COST'), columnspan: 1 },
          { headerText: `${this.decimalPrecisionPipe.transform(totalTdcPercent)}%`, columnspan: 1, alignment: "right" },
          { headerText: this.multiLingualPipe.transform(totalCost), columnspan: 1, alignment: "right" },
          { headerText: this.multiLingualPipe.transform(totalEscalation), columnspan: 1, alignment: "right" },
          { headerText: this.multiLingualPipe.transform(totalGm), columnspan: 1, alignment: "right" },
          { headerText: this.multiLingualPipe.transform(totalSellPrice), columnspan: 1, alignment: "right" }
        ]
      },
    ]
  }

  formatMaterialDataRows(reportsDataCollection: Record<string, MaterialReportData>): IData[] {
    let materialDataRows: IData[] = [];
    for (let materialKey in reportsDataCollection) {
      materialDataRows.push(this.createRowData(reportsDataCollection[materialKey]));
    }
    return materialDataRows
  }

  materialTableCreation(headerName: string, reportsDataCollection: Record<string, MaterialReportData>): void {
    let materialDataRows: IData[] = this.formatMaterialDataRows(reportsDataCollection);
    if (!materialDataRows.length) {
      return;
    }
    let materialData: ITableData = {
      title: this.createTableSubHeader(),
      tableHeader: [
        {
          headers: [
            { headerText: this.translate.instant(headerName), columnspan: this.selectedReportCheckboxes.expanded.checked ? 7 : 5, italics: true }
          ]
        }
      ],
      data: materialDataRows,
      tableFooter: this.calculateTotalCost(reportsDataCollection),
      isTableBordered: true
    };
    this.materialByTypeData.push(materialData);
  }

  calculateTotalCost(MaterialReportDataCollection: { [key: string]: MaterialReportData }): IFooter[] {
    let totalCost: number = 0;
    for (let materialKey in MaterialReportDataCollection) {
      totalCost += MaterialReportDataCollection[materialKey].totalCost;
    }
    if (this.selectedReportCheckboxes.expanded.checked) {
      return [
        {
          headers: [
            { headerText: this.translate.instant('MATERIAL_REPORT_TOTAL_COST'), columnspan: CONSTANTS.ONE },
            { headerText: '', columnspan: CONSTANTS.ONE, alignment: 'center' },
            { headerText: '', columnspan: CONSTANTS.ONE, alignment: 'center' },
            { headerText: '', columnspan: CONSTANTS.ONE, alignment: 'center' },
            { headerText: '', columnspan: CONSTANTS.ONE, alignment: 'center' },
            { headerText: '', columnspan: CONSTANTS.ONE, alignment: 'center' },
            { headerText: this.multiLingualPipe.transform(totalCost), columnspan: CONSTANTS.ONE, alignment: "right" }
          ]
        }
      ]

    } else {
      return [
        {
          headers: [
            { headerText: this.translate.instant('MATERIAL_REPORT_TOTAL_COST'), columnspan: CONSTANTS.ONE },
            { headerText: '', columnspan: CONSTANTS.ONE, alignment: 'center' },
            { headerText: '', columnspan: CONSTANTS.ONE, alignment: 'center' },
            { headerText: '', columnspan: CONSTANTS.ONE, alignment: 'center' },
            { headerText: this.multiLingualPipe.transform(totalCost), columnspan: CONSTANTS.ONE, alignment: "right" }
          ]
        },
      ]
    }
  }

  createSummaryRowData(materialSummaryData: MaterialSummaryData): IData {
    let rowData: IData = {
      data: [
        { data: materialSummaryData.materialName, isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: "left" },
        { data: `${this.decimalPrecisionPipe.transform(materialSummaryData.tdcPercent)}%`, isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: "right", isExpanded: true },
        { data: this.multiLingualPipe.transform(materialSummaryData.cost), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: "right", isExpanded: true },
        { data: this.multiLingualPipe.transform(materialSummaryData.escalation), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: "right" },
        { data: this.multiLingualPipe.transform(materialSummaryData.gm), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: "right" },
        { data: this.multiLingualPipe.transform(materialSummaryData.sellPrice), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: "right", DataCellColor: CONSTANTS.REPORT_HIGHLIGHT_COLOR }
      ]
    };
    return rowData;
  }

  createRowData(materialReportData: MaterialReportData): IData {
    let rowData: IData = {
      data: [
        { data: materialReportData.referenceNo, isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: "left" },
        { data: materialReportData.modelNo, isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: "left", isExpanded: true },
        { data: materialReportData.manufacturerPartNo, isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: "left", isExpanded: true },
        { data: materialReportData.description, isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: "left" },
        { data: materialReportData.quantity.toString(), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: "center" },
        { data: this.multiLingualPipe.transform(materialReportData.unitCost), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: "right" },
        { data: this.multiLingualPipe.transform(materialReportData.totalCost), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: "right", DataCellColor: CONSTANTS.REPORT_HIGHLIGHT_COLOR }
      ]
    };
    return rowData;
  }

  createTableSubHeader(): IDataItem[] {
    return [
      { data: this.translate.instant('MATERIAL_REPORT_REFERENCE'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0 },
      { data: this.translate.instant('MATERIAL_REPORT_MODEL'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, isExpanded: true },
      { data: this.translate.instant('MATERIAL_REPORT_MANUFACTURER_PART'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, isExpanded: true },
      { data: this.translate.instant('MATERIAL_REPORT_DESCRIPTION'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, width: "130" },
      { data: this.translate.instant('QTY'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0 },
      { data: this.translate.instant('MATERIAL_REPORT_UNIT_COST'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0 },
      { data: this.translate.instant('MATERIAL_REPORT_TOTAL_COST'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0 }
    ]
  }

  ngOnDestroy(): void {
    this.projectAreas = [];
    this.projectMaterialData = {};
    this.materialByPartTypeData = [];
    this.materialByTypeData = [];
    this.serviceContractMaterialData = [];
    this.formattedMaterialReportData = {};
    this.siemensMaterialReportData = {};
    this.siOemMaterialReportData = {};
    this.outsideMaterialReportData = {};
    this.mechanicalMaterialReportData = {};
    this.electricalMaterialReportData = {};
    this.collectionByPartType = {};
    this.consolidatedMaterialSummaryData = {};
    this.partTypeIdNameMap = {};
    this.ssnWithIVKMap = {};
    this.estimateMetaData = {} as EstimateMetaData;
    this.grossMargin = {} as EstimateGrossMargin;
    this.selectedReportCheckboxes = {} as ReportCheckboxes;
    this.currentReportId = 0 as ReportList;
  }

}

